import React from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PercentIcon from "@mui/icons-material/Percent";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const steps = [
  { label: "Project Scope", sub: "Select projects" },
  { label: "Brokerage Type", sub: "Choose calculation" },
  { label: "Status", sub: "Set status" },
  { label: "Review", sub: "Confirm & save" },
];

const CreateBasicBrokeragePlanStep2 = ({
  data,
  setData,
  onBack,
  onNext,
}) => {
  const { brokerageType, percentage, fixedAmount } = data;

  const isInvalidPercentage =
    brokerageType === "percentage" &&
    (!percentage || percentage <= 0 || percentage > 100);

  const isInvalidFixed =
    brokerageType === "fixed" &&
    (!fixedAmount || fixedAmount <= 0);

  const formatIndianNumber = (value) => {
    if (!value) return "";
    return Number(value).toLocaleString("en-IN");
  };

  const cleanNumber = (value) => {
    return value.replace(/[^0-9]/g, "");
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>

      {/* TOP BAR */}
      <Box display="flex" justifyContent="space-between" mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ color: "#2563EB", textTransform: "none" }}
          onClick={onBack}
        >
          Back to Plans
        </Button>
      </Box>

      {/* STEPPER */}
      <Stepper
        activeStep={1}
        alternativeLabel
        sx={{
          mb: 6,
          "& .MuiStepIcon-root": { fontSize: "3rem" },
          "& .MuiStepIcon-root.Mui-active": { color: "#2563EB" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#2563EB" },
          "& .MuiStepLabel-label": {
            fontSize: "15px",
            fontWeight: 600,
            marginTop: "8px",
          },
          "& .MuiStepConnector-line": {
            borderTopWidth: 3,
            borderColor: "#E5E7EB",
          },
        }}
      >
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography fontWeight={600}>{step.label}</Typography>
              <Typography fontSize={13} color="text.secondary">
                {step.sub}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* MAIN GRID */}
      <Box display="grid" gridTemplateColumns="2fr 1fr" gap={4}>

        {/* LEFT SECTION */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: 3,
            p: 4,
            border: "1px solid #E5E7EB",
          }}
        >
          <Typography fontSize={18} fontWeight={600} mb={3}>
            Brokerage Type
          </Typography>

          {/* TYPE CARDS */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3} mb={4}>

            {/* Percentage */}
            <Box
              onClick={() =>
                setData({
                  ...data,
                  brokerageType: "percentage",
                  fixedAmount: "",
                })
              }
              sx={{
                p: 3,
                borderRadius: 3,
                cursor: "pointer",
                border: "1px solid",
                borderColor:
                  brokerageType === "percentage"
                    ? "#2563EB"
                    : "#E5E7EB",
                background:
                  brokerageType === "percentage"
                    ? "#EFF6FF"
                    : "#fff",
                transition: "0.2s",
                "&:hover": {
                  borderColor: "#2563EB",
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: "#E0E7FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <PercentIcon sx={{ color: "#2563EB" }} />
              </Box>

              <Typography fontWeight={600}>
                Percentage Based
              </Typography>
              <Typography fontSize={13} color="text.secondary">
                Calculated on agreement value
              </Typography>
            </Box>

            {/* Fixed */}
            <Box
              onClick={() =>
                setData({
                  ...data,
                  brokerageType: "fixed",
                  percentage: "",
                })
              }
              sx={{
                p: 3,
                borderRadius: 3,
                cursor: "pointer",
                border: "1px solid",
                borderColor:
                  brokerageType === "fixed"
                    ? "#2563EB"
                    : "#E5E7EB",
                background:
                  brokerageType === "fixed"
                    ? "#EFF6FF"
                    : "#fff",
                transition: "0.2s",
                "&:hover": {
                  borderColor: "#2563EB",
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: "#F1F5F9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <PaymentsOutlinedIcon />
              </Box>

              <Typography fontWeight={600}>
                Fixed Amount
              </Typography>
              <Typography fontSize={13} color="text.secondary">
                Same amount for every agreement
              </Typography>
            </Box>
          </Box>

          {/* INPUT SECTION */}
          {brokerageType === "percentage" && (
            <>
              <Typography fontWeight={500} mb={1}>
                Percentage (%)
              </Typography>

              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 100 }}
                value={percentage}
                onChange={(e) =>
                  setData({ ...data, percentage: e.target.value })
                }
              />

              <Typography fontSize={13} color="text.secondary" mt={1}>
                Enter percentage of agreement value
              </Typography>
            </>
          )}

          {brokerageType === "fixed" && (
            <>
              <Typography fontWeight={500} mb={1}>
                Fixed Amount (₹)
              </Typography>

              <TextField
                fullWidth
                value={formatIndianNumber(fixedAmount)}
                onChange={(e) => {
                  const cleaned = cleanNumber(e.target.value);
                  setData({ ...data, fixedAmount: cleaned });
                }}
                inputProps={{ inputMode: "numeric" }}
              />

              <Typography fontSize={13} color="text.secondary" mt={1}>
                Enter fixed brokerage amount
              </Typography>
            </>
          )}
        </Box>

        {/* RIGHT SIDE */}
        <Box display="flex" flexDirection="column" gap={3}>

          {/* Live Preview */}
          {/* <Box
            sx={{
              background: "#fff",
              borderRadius: 3,
              p: 3,
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography fontWeight={600} mb={2}>
              Live Preview
            </Typography>

            <Typography fontSize={14}>
              Agreement Value: <b>₹5.00 Cr</b>
            </Typography>

            <Typography fontSize={14}>
              Base Brokerage:{" "}
              <b>
                {brokerageType === "percentage"
                  ? `${percentage || 0}%`
                  : `₹${fixedAmount || 0}`}
              </b>
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography fontWeight={600} fontSize={16}>
              Brokerage: ₹— —
            </Typography>
          </Box> */}

          {/* Business Rules */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: 3,
              p: 3,
              border: "1px solid #E5E7EB",
            }}
          >
            <Typography fontWeight={600} mb={2}>
              Business Rules
            </Typography>

            {[
              "Calculated after agreement completion",
              "Paid immediately",
              "Cannot be overridden",
              "Always shown on CP dashboard",
            ].map((rule) => (
              <Box key={rule} display="flex" gap={1.5} mb={1.5}>
                <CheckCircleIcon sx={{ color: "#16A34A", fontSize: 18 }} />
                <Typography fontSize={13}>{rule}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box display="flex" justifyContent="space-between" mt={6}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>

        <Button
          variant="contained"
          onClick={onNext}
          disabled={isInvalidPercentage || isInvalidFixed}
        >
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default CreateBasicBrokeragePlanStep2;